import type { CSSProperties } from 'react';
import {
  addDays,
  MS_PER_DAY,
  occurrenceOwnerIds,
  startOfDay,
  type EventOccurrence,
} from '@awwyeah/shared';
import { styleForOwners, type MemberView } from './members';

/**
 * The per-day dot model shared by the month grid and the week header strip.
 *
 * `occurrenceOwnerIds` only reports *owners*, so an event with `ownerIds: []` (every
 * imported feed entry, and anything saved without picking a person) used to contribute
 * nothing at all — a busy imported day looked empty apart from a bare "+1". Usability
 * testing 2026-08-01 turned that up while asking "what does this mark mean?", so an
 * ownerless day now gets one explicit hollow "unassigned" dot instead.
 */

/** Sentinel `ownerId` for the hollow dot that stands for "nobody in particular". */
export const UNASSIGNED_DOT = '__unassigned__';

export interface DayDots {
  /** Owner ids to draw, plus at most one `UNASSIGNED_DOT`, already capped. */
  dots: string[];
  /** Marks that didn't fit — surfaced as "+N". */
  hidden: number;
}

export function dayDots(occurrences: readonly EventOccurrence[], limit = 4): DayDots {
  const owners = occurrenceOwnerIds(occurrences);
  const hasUnassigned = occurrences.some((occurrence) => occurrence.event.ownerIds.length === 0);
  const all = hasUnassigned ? [...owners, UNASSIGNED_DOT] : owners;
  return { dots: all.slice(0, limit), hidden: Math.max(0, all.length - limit) };
}

export function isUnassignedDot(id: string): boolean {
  return id === UNASSIGNED_DOT;
}

/**
 * The month cell's packed summary (2026-09 — Faye had four events on a day that showed
 * one pink dot). Compact month views on phones stack a few thin owner-coloured bars, one
 * per *event* rather than one per person, so "busy" reads at a glance; wider screens show
 * truncated titles instead. Both draw from this same ordered, capped list.
 */
export interface DayMark {
  occurrence: EventOccurrence;
  /** All-day, or a timed event covering 24h+ — drawn as a band, not a timed line. */
  band: boolean;
  /** Began on an earlier day / runs on past this one — the bar squares off that side. */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface DaySummary {
  /** What fits, in draw order. */
  marks: DayMark[];
  /** Events folded into the quiet "+N". */
  hidden: number;
  total: number;
}

/**
 * Orders a day's events band-first (longest span first, so a week-long trip keeps roughly
 * the same row across the days it covers), then timed events by start, then title and id
 * for a stable tie-break across devices.
 *
 * With `overflowTakesSlot` (the phone bars), when everything doesn't fit the last slot
 * goes to "+N" rather than to one more event — so the overflow is always at least 2 and
 * never trades a lone bar for "+1". Without it (the titled cells), `limit` events show
 * and "+N more" rides on its own line underneath.
 */
export function daySummary(
  occurrences: readonly EventOccurrence[],
  day: Date,
  limit: number,
  { overflowTakesSlot = true }: { overflowTakesSlot?: boolean } = {},
): DaySummary {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = addDays(startOfDay(day), 1).getTime();
  const marks = occurrences.map((occurrence): DayMark & { span: number; startMs: number } => {
    const startMs = Date.parse(occurrence.start);
    const endMs = Date.parse(occurrence.end);
    const span = endMs - startMs;
    return {
      occurrence,
      band: occurrence.event.allDay || span >= MS_PER_DAY,
      continuesBefore: startMs < dayStart,
      continuesAfter: endMs > dayEnd,
      span,
      startMs,
    };
  });
  marks.sort(
    (a, b) =>
      Number(b.band) - Number(a.band) ||
      (a.band ? b.span - a.span : 0) ||
      a.startMs - b.startMs ||
      a.occurrence.event.title.localeCompare(b.occurrence.event.title) ||
      a.occurrence.event.id.localeCompare(b.occurrence.event.id),
  );
  const total = marks.length;
  const shown = total <= limit ? total : Math.max(0, overflowTakesSlot ? limit - 1 : limit);
  return {
    marks: marks.slice(0, shown).map(({ occurrence, band, continuesBefore, continuesAfter }) => ({
      occurrence,
      band,
      continuesBefore,
      continuesAfter,
    })),
    hidden: total - shown,
    total,
  };
}

/**
 * Fill for a month-cell bar: the owner's colour, or — for a shared event — hard stripes
 * of up to three owners' colours side by side, so "Dan & Faye" isn't just "Dan".
 * Ownerless events keep the unassigned treatment (pair with `UNASSIGNED_DOT_CLASS`).
 */
export function barFill(
  ownerIds: readonly string[],
  byId: ReadonlyMap<string, MemberView>,
): CSSProperties {
  const colors = ownerIds
    .map((id) => byId.get(id)?.color)
    .filter((color): color is string => !!color)
    .slice(0, 3);
  if (colors.length < 2) return styleForOwners(ownerIds, byId).dot;
  const step = 100 / colors.length;
  const stops = colors.map(
    (color, index) => `${color} ${(index * step).toFixed(2)}% ${((index + 1) * step).toFixed(2)}%`,
  );
  return { backgroundImage: `linear-gradient(90deg, ${stops.join(', ')})` };
}
